import type { RegistryState, RiskRating } from '../../data/types';
import { nextReviewDate } from '../../domain/kyc/riskRating';
import { newId } from '../../lib/random';
import { actorName, holderName, today, updateHolder, withAudit } from '../helpers';

export function recordRiskReview(
  state: RegistryState,
  action: {
    holderId: string;
    rating: RiskRating;
    suggested: RiskRating;
    factors: string[];
    notes: string;
  },
): RegistryState {
  const review = {
    id: newId('rr'),
    date: today,
    rating: action.rating,
    suggested: action.suggested,
    factors: action.factors,
    notes: action.notes,
    reviewedBy: actorName(state),
    nextReviewDate: nextReviewDate(action.rating, today),
  };
  const next = updateHolder(state, action.holderId, (holder) => ({
    riskRating: action.rating,
    riskReviews: [review, ...(holder.riskReviews ?? [])],
  }));
  return withAudit(
    next,
    'Risk rating reviewed',
    holderName(state, action.holderId),
    action.rating === action.suggested
      ? `${action.rating} (as suggested)`
      : `${action.rating} (suggested ${action.suggested}): ${action.notes}`,
  );
}

export function verifySourceOfWealth(
  state: RegistryState,
  action: { holderId: string; personId: string },
): RegistryState {
  const next = updateHolder(state, action.holderId, (holder) =>
    action.personId === holder.id
      ? { sourceOfWealthVerifiedAt: today }
      : {
          beneficialOwners: holder.beneficialOwners.map((owner) =>
            owner.id === action.personId ? { ...owner, sourceOfWealthVerifiedAt: today } : owner,
          ),
        },
  );
  return withAudit(
    next,
    'Source of wealth verified',
    holderName(state, action.holderId),
    'Evidence reviewed and filed',
  );
}

export function overrideWholesale(
  state: RegistryState,
  action: { holderId: string; qualified: boolean; reason: string },
): RegistryState {
  const next = updateHolder(state, action.holderId, () => ({
    wholesaleOverride: {
      qualified: action.qualified,
      reason: action.reason,
      by: actorName(state),
      at: today,
    },
  }));
  return withAudit(
    next,
    action.qualified ? 'Wholesale status set by hand' : 'Wholesale status removed by hand',
    holderName(state, action.holderId),
    action.reason,
  );
}
